import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getItemWebStorage } from "../../../../features/webStorageSaver";
import { MapTooltip } from "../../../../widgets/map/mapAddons/mapTooltip";
import { getBasicObjectTypeLayer, getCameraMarkerLayer, getPanoramaVideoLayer, getAllObjectsLayer } from "../../../../widgets/map/mapFeatures/layers";
import MapWidget from "../../../../widgets/map/mapWidget/MapWidget";
import { DiagnosticsYearFilters } from "../../../../widgets/diagnosticsYearFilters";
import { useAppSelector } from "../../../../stateManagement/hooks/redux";
import { shortMeasureTypesNames } from "../../../../widgets/diagnostics/diagnosticsPathNormativitiesSection";
import { copyInfoFromTooltip, showMapTooltip } from "../../../../widgets/map/mapFeatures/mapFunctions/showMapTooltip/showMapTooltip";
import { setMapTooltipIsVisible } from "../../../../stateManagement/reducers/MapTooltipSlice";
import { useLazyFetchPathNormativitiesQuery } from "../../../../api/rtk/base/slices/diagnosticsApiSlice";
import { setPathNormativitiesForMapTooltip } from "../../../../stateManagement/reducers/diagnostics/PathNormativitiesSlice";
import { MapOverlayWidget } from "../../../../widgets/map/mapAddons/mapOverlayWidget";
import { getCameraMarkerStyle } from "../../../../widgets/map/mapFeatures/styles/mapStyles";
import { configurePanoramaFilters } from "../../../../widgets/map/mapFeatures/helpers";
import { fromLonLat, toLonLat } from "ol/proj";
import { getFrameOnMapPoint, getFrameOnMapPointAfterDragAndDrop, positionIsInMapView } from "../../../../widgets/map/mapFeatures/mapFunctions/getFrameOnMapPoint/getFrameOnMapPoint";
import { useLazyFetchFrameInPointQuery } from "../../../../api/rtk/base/slices/panoramaApiSlice";
import VideoFramesWidget from "../../../../widgets/panorama/videoFramesWidget/VideoFramesWidget";
import PanoramaWidget from "../../../../widgets/panorama/panoramaWidget/PanoramaWidget";
import { Loader } from "../../../../widgets/loaders/Loader";
import { getModifiedCameraCircle } from "../../../../widgets/map/mapFeatures/interactions";
import { unByKey } from "ol/Observable";
import { mapLayersDict } from "../../mapLayersDict";
import { setHighContrastModeIsEnabled } from "../../../../stateManagement/reducers/ContentHeaderSlice";
import ContentHeader from "../../../../widgets/contentHeader/ContentHeader";

export function MapPathNormativitiesMeasureType() {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { measureType } = useParams()
    const { wmsServer, wmsVersion, workspace, panoStart } = getItemWebStorage('currentConfig')
    const objectTypeName = "PathNormativitiesMeasureType"
    const [isSmallWindow, setIsSmallWindow] = useState(false);
    const [mapTitle, setMapTitle] = useState<string>('');

    const { cameraMarkerLongitude, cameraMarkerLatitude, showCameras, panoramaXMLIsLoading, panoramaCurrentHLookAngle, panoramaCurrentAzimuth,
        panoramaWindowIsLoading, panoramaVideoIsPlaying } = useAppSelector(state => state.panorama)
    const cameraLayerLongitude = cameraMarkerLongitude ?? panoStart[0]
    const cameraLayerLatitude = cameraMarkerLatitude ?? panoStart[1]
    const isLoading = (!showCameras && panoramaWindowIsLoading && !panoramaVideoIsPlaying) || (showCameras && panoramaXMLIsLoading && !panoramaVideoIsPlaying)
    const [cameraMarkerLayer] = useState(getCameraMarkerLayer([cameraLayerLongitude, cameraLayerLatitude], getCameraMarkerStyle(panoramaCurrentHLookAngle + panoramaCurrentAzimuth)))
    const [panoramaVideoLayer, setPanoramaVideoLayer] = useState<any>()
    const [centerOnObjectCoordinates, setCenterOnObjectCoordinates] = useState<any>();
    const [additionalLayers, setAdditionalLayers] = useState<any>([]);
    const [mapEventHandlers, setMapEventHandlers] = useState<any>([])
    const [mapInteractions, setMapInteractions] = useState<any>([])
    const sideBarState = useAppSelector(state => state.sidebar.sidebarIsShown)
    const yearFilter = useAppSelector(state => state.diagnosticsPathNormativities.pathNormativitiesMapYearFilter)
    const mapFilterString = useAppSelector(state => state.diagnosticsPathNormativities.pathNormativitiesMapFilterString)
    const showPanorama = useAppSelector(state => state.contentHeader.panoramaUnderMapIsShown)
    const [fetchPathNormativitiesInfo] = useLazyFetchPathNormativitiesQuery()
    const [fetchFrameInPointQuery] = useLazyFetchFrameInPointQuery();

    const roadsLayer = getAllObjectsLayer()
    const [permanentLayers] = useState([roadsLayer])

    useEffect(() => {
        const { mapLayer, caption, styles } = mapLayersDict[objectTypeName] ?? {}
        if (measureType && caption) {
            setMapTitle(`Карта «${caption} (${shortMeasureTypesNames[measureType]})»`)
        }
        if (!measureType) {
            navigate('/app/error')
        }
        let layerFilter = `DiagnosticYear in (${yearFilter})`
        layerFilter += mapFilterString
        const style = styles?.find((style: any) => style.type === measureType)?.style
        const objectTypeLayer: any = getBasicObjectTypeLayer(mapLayer, wmsServer, wmsVersion, workspace, layerFilter, style)
        let additionalLayers = [objectTypeLayer]
        if (panoramaVideoLayer && showPanorama) {
            additionalLayers.push(panoramaVideoLayer)
        }
        if (cameraMarkerLayer && showPanorama) {
            additionalLayers.push(cameraMarkerLayer)
        }
        setAdditionalLayers(additionalLayers)
        dispatch(setHighContrastModeIsEnabled(true))
    }, [measureType, yearFilter, mapFilterString, panoramaVideoLayer, cameraMarkerLayer, showPanorama]);

    useEffect(() => {
        if (showPanorama) {
            getFrameOnMapPoint({ fetchFrameInPointQuery, argsLongitude: cameraLayerLongitude, argsLatitude: cameraMarkerLatitude, isAfterLayerChange: true, argsYear: yearFilter })
        }
    }, [showPanorama, panoramaVideoLayer])

    // при изменении года запрашивать участки нормативности для подсказки
    useEffect(() => {
        getPathNormativitiesForCurrentYear()
    }, [yearFilter])

    useEffect(() => {
        const contentHeaderWidth: any = document.querySelector('.content-header-div')?.getBoundingClientRect().width;
        setIsSmallWindow(measureType === 'Total' ? contentHeaderWidth < 720 : contentHeaderWidth < 400);
    }, [sideBarState, navigate])

    useEffect(() => {
        const contentHeaderWidth: any = document.querySelector('.content-header-div')?.getBoundingClientRect().width;
        const hasSeenYearForMobile = getItemWebStorage('diagnosticsYearFilters')?.pathNormativitiesMapSeen
        if (!hasSeenYearForMobile && (measureType === 'Total' ? contentHeaderWidth < 720 : contentHeaderWidth < 400)) {
            navigate('/app/map/PathNormativities/filters')
        }
    }, [])

    useEffect(() => {
        let layerFilter: any = configurePanoramaFilters(null, null, yearFilter)
        const panoramaVideoLayer = getPanoramaVideoLayer(wmsServer, wmsVersion, workspace, layerFilter)
        setPanoramaVideoLayer(panoramaVideoLayer)
    }, [yearFilter])

    useEffect(() => {
        if (cameraMarkerLongitude && cameraMarkerLatitude) {
            let coordinates = fromLonLat([cameraMarkerLongitude, cameraMarkerLatitude])
            cameraMarkerLayer?.getSource()?.getFeatures()[0]?.getGeometry()?.setCoordinates(coordinates)
            if (!positionIsInMapView(coordinates)) {
                setCenterOnObjectCoordinates(coordinates)
            }
        }
    }, [cameraMarkerLongitude, cameraMarkerLatitude])

    useEffect(() => {
        const rotateAngle = panoramaCurrentHLookAngle + panoramaCurrentAzimuth
        cameraMarkerLayer?.getSource()?.getFeatures()[0]?.setStyle(getCameraMarkerStyle(rotateAngle))
    }, [panoramaCurrentHLookAngle, panoramaCurrentAzimuth])

    // функция для запроса участков нормативности для подсказки
    async function getPathNormativitiesForCurrentYear() {
        let filterStringArray = [`DiagnosticYear eq ${yearFilter}`]
        const response = await fetchPathNormativitiesInfo({ filterStringArray })
        if (response?.data?.value) {
            dispatch(setPathNormativitiesForMapTooltip(response.data.value))
        }
    }

    function applyMapTooltip(map: any) {
        const pointerMoveFunc = (event: any) => showMapTooltip(event, 'pathNormativities', additionalLayers[0], map, measureType)
        const activeTooltipClick = (event: any) => copyInfoFromTooltip(measureType)
        map?.on("pointermove", pointerMoveFunc)
        map?.on("singleclick", activeTooltipClick)
        return {
            off: () => {
                map?.un("pointermove", pointerMoveFunc)
                map?.un("singleclick", activeTooltipClick)
                dispatch(setMapTooltipIsVisible(false))
            }
        }
    }

    function applyMapFrameFind(map: any) {
        const clickFunc = (event: any) => {
            const [longitude, latitude] = toLonLat(event?.coordinate)
            getFrameOnMapPoint({ fetchFrameInPointQuery, argsLongitude: longitude, argsLatitude: latitude, argsYear: yearFilter })
        }
        map?.on("singleclick", clickFunc)
        return {
            off: () => {
                map?.un("singleclick", clickFunc)
            }
        }
    }

    useEffect(() => {
        const mapEventHandlers = [
            {
                name: "mapTooltip",
                func: applyMapTooltip
            }
        ]
        if (showPanorama) {
            mapEventHandlers.push(
                {
                    name: "frameFind",
                    func: applyMapFrameFind
                }
            )
        }
        setMapEventHandlers(mapEventHandlers)
    }, [showPanorama, yearFilter, additionalLayers, measureType])

    function applyModifyPanoramaCameraInteraction(map: any) {
        // const modifyCameraView = getModifiedCameraView(cameraMarkerLayer, cameraMarkerLayer.getSource())
        const modifyCameraCircle = getModifiedCameraCircle(cameraMarkerLayer.getSource())
        const cameraCircleModifyEndFunc: any = (event: any) => getFrameOnMapPointAfterDragAndDrop({ fetchFrameInPointQuery, cameraMarkerLayer})
        modifyCameraCircle.on('modifyend', cameraCircleModifyEndFunc);
        map.addInteraction(modifyCameraCircle);
        // map.addInteraction(modifyCameraView);
        return {
            off: () => {
                map.removeInteraction(modifyCameraCircle);
                unByKey(cameraCircleModifyEndFunc)
            }
        }
    }

    useEffect(() => {
        const mapInteractions = [
            {
                name: "modifyPanoramaCamera",
                func: applyModifyPanoramaCameraInteraction
            }
        ]
        setMapInteractions(mapInteractions)
    }, [])

    return (
        <>
            <ContentHeader title={mapTitle} objectTypeName={objectTypeName} type="map" option={`pathNormativitiesMeasureType${measureType === 'Total' ? 'Total' : ''}`} />
            {!isSmallWindow && <DiagnosticsYearFilters type="header" filterType="PathNormativitiesMapMeasureType" />}
            {showPanorama &&
                <div className="panorama-div half-with-filter-row">
                    {isLoading && <Loader />}
                    {showCameras
                        ?
                        <VideoFramesWidget />
                        :
                        <PanoramaWidget />
                    }
                </div>
            }
            <div className={`map-widget-with-toast${!isSmallWindow ? ' with-year-filter-row' : ''}${showPanorama ? ' half-size' : ''}`}>
                {isLoading && <Loader />}
                <MapTooltip type={objectTypeName} />
                <MapWidget
                    additionalLayers={additionalLayers}
                    permanentLayers={permanentLayers}
                    mapEventHandlers={mapEventHandlers}
                    mapInteractions={mapInteractions}
                    mapType={showPanorama ? "panorama" : null}
                    centerOnObjectCoordinates={centerOnObjectCoordinates}
                />
                <MapOverlayWidget />
            </div>
        </>
    )
}
